import { initPlayerTelemetry } from '@viewlift/telemetry-sdk';
import { getWebTelemetry } from '@viewlift/telemetry-sdk/web';
import md5 from 'md5';

export type ShakaTelemetryInitConfig = Record<string, unknown>;
export type VideoJSTelemetryInitConfig = Record<string, unknown>;
export type BitmovinTelemetryInitConfig = Record<string, unknown>;

/** Shape returned by VL player `init()` and forwarded through telemetry Integration.init(). */
export type PlayerTelemetryInitResult = {
  response?: {
    playable?: boolean;
    plans?: Array<{
      id?: string;
      planIds?: unknown[];
      featureSetting?: { isLoginRequired?: boolean; isEmailRequired?: boolean };
    }>;
    video?: {
      streamingInfo?: {
        audioAssets?: { hls?: unknown };
        videoAssets?: {
          playReady?: { url: string };
          widevine?: { url: string };
        };
      };
      monetizationModels?: Array<{ type?: string }>;
      plans?: Array<{ planIds?: unknown[] }>;
      gist?: { id?: string; title?: string; contentType?: string };
      metadata?: Array<{ name?: string; value?: unknown }>;
    };
    linearchannel?: {
      monetizationModels?: Array<{ type?: string }>;
      plans?: Array<{ planIds?: unknown[] }>;
    };
  };
};

/**
 * Initializes Shaka via telemetry SDK (ShakaIntegration.init). Events POST to `telemetryEndpoint`
 * (defaults to {@link getTelemetryIngestEndpoint}).
 */
type AppCmsWindow = Window & {
  /** Injected by SSR from process.env.TELEMETRY_INGEST_ENDPOINT. Origin only, no /telemetry. */
  TELEMETRY_INGEST_ENDPOINT?: string;
  /** Injected by SSR in `client/html.tsx`. Not available at module-eval on the server. */
  toolsUrl?: string;
  /** Lowercase key used by some pages for geo (telemetry reads this first after explicit option). */
  countrycode?: string;
  /** Set from page/bootstrap in `client/helpers.js` and Masthead helper. */
  countryCode?: string;
  /** SSR request id from `window.requestId`. Sent as `vl.request.id`. */
  requestId?: string;
  /** Optional platform override. Defaults to `web` for this host. */
  platform?: string;
  app_data?: {
    countryCode?: string;
    appcmsMain?: {
      id?: string | number;
      site?: string;
      features?: { customReceiverId?: string; isAirPlayEnabled?: boolean };
    };
  };
};

type PlayerCoreWithGetPlayer = {
  init: (config: unknown) => Promise<unknown>;
  getPlayer?: (id?: string) => unknown;
  [key: string]: unknown;
};

const readWindow = (): AppCmsWindow | undefined =>
  typeof window !== 'undefined' ? (window as AppCmsWindow) : undefined;

const telemetryIngestBase = (): string => {
  const configured = readWindow()?.TELEMETRY_INGEST_ENDPOINT;
  if (typeof configured !== 'string') return '';
  return configured.trim().replace(/\/+$/, '');
};

/**
 * Ingest URL: `TELEMETRY_INGEST_ENDPOINT` + `/telemetry`.
 * The server reads `process.env.TELEMETRY_INGEST_ENDPOINT` and injects it onto `window`.
 */
export const getTelemetryIngestEndpoint = (): string => {
  const base = telemetryIngestBase();
  if (!base) return '';
  if (base.endsWith('/telemetry')) return base;
  return `${base}/telemetry`;
};

/** Origin from `TELEMETRY_INGEST_ENDPOINT`, without the `/telemetry` path. */
export const getTelemetryOtlpEndpoint = (): string | undefined => {
  const base = telemetryIngestBase();
  return base || undefined;
};

type VideoJsGlobal = {
  getPlayer?: (id: string) => unknown;
  getPlayers?: () => Record<string, unknown>;
};

const readVideoJs = (): VideoJsGlobal | undefined => {
  const w = readWindow() as (AppCmsWindow & { videojs?: VideoJsGlobal }) | undefined;
  return w?.videojs;
};

export const resolvePlayerInstanceForTelemetry = (
  getPlayer: ((id?: string) => unknown) | undefined,
  playerId: string,
): unknown => {
  const fromId = getPlayer?.(playerId);
  if (fromId) return fromId;
  const fromDefault = getPlayer?.();
  if (fromDefault) return fromDefault;
  if (typeof document !== 'undefined') {
    const el = document.getElementById(playerId) as (HTMLElement & { player?: unknown }) | null;
    if (el?.player) return el.player;
  }
  const videojs = readVideoJs();
  const fromVjs = videojs?.getPlayer?.(playerId) || videojs?.getPlayers?.()?.[playerId];
  return fromVjs || null;
};

const waitForPlayerInstance = async (
  getPlayer: ((id?: string) => unknown) | undefined,
  playerId: string,
): Promise<unknown> => {
  const immediate = resolvePlayerInstanceForTelemetry(getPlayer, playerId);
  if (immediate) return immediate;
  if (typeof document === 'undefined' || !document.getElementById(playerId)) return null;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    const found = resolvePlayerInstanceForTelemetry(getPlayer, playerId);
    if (found) return found;
  }
  return null;
};

/**
 * `@viewlift/telemetry-sdk` 1.0.11 throws if `getPlayer(id)` is falsy after
 * `init()`, which hides a player that already mounted. VL's VideoJS helper
 * also returns boolean `false` (not null) on a miss, and Bitmovin looks up
 * with no id. Normalize those lookups so telemetry attach cannot tear down
 * playback.
 */
export const bindPlayerCoreForTelemetry = (vlCore: unknown, playerId: string): unknown => {
  if (vlCore == null || typeof vlCore !== 'object') return vlCore;
  const core = vlCore as PlayerCoreWithGetPlayer;
  const originalGetPlayer =
    typeof core.getPlayer === 'function' ? core.getPlayer.bind(core) : undefined;
  return {
    ...core,
    getPlayer: (id?: string) =>
      resolvePlayerInstanceForTelemetry(originalGetPlayer, id != null && String(id).trim() !== '' ? String(id) : playerId),
  };
};

type TelemetryIntegration = {
  init: () => Promise<unknown>;
  adapter?: { init?: () => void };
  destroy?: () => Promise<void>;
};

type PlayerKind = 'shaka' | 'videojs' | 'bitmovin';

export function readTelemetryRequestId(): string | undefined {
  const id = readWindow()?.requestId;
  if (id == null) return undefined;
  const trimmed = String(id).trim();
  return trimmed || undefined;
}

/** Host platform. `window.platform` wins; this web app otherwise sends `web`. */
export function readTelemetryPlatform(): string {
  const platform = readWindow()?.platform;
  if (platform != null && String(platform).trim() !== '') return String(platform).trim();
  return 'web';
}

/**
 * Start the VL player first, then attach telemetry to the live instance.
 * Telemetry 1.0.11 can throw after a successful `init()` (missing getPlayer,
 * adapter/tech errors). Playback must continue even if attach fails.
 */
export async function initPlayerThenAttachTelemetry<TIntegration, TResult>(options: {
  playerId: string;
  vlCoreFactory: () => unknown;
  initConfig: Record<string, unknown>;
  createIntegration: (vlPlayerCore: unknown) => TelemetryIntegration;
}): Promise<{ integration: TIntegration | null; result: TResult }> {
  const core = bindPlayerCoreForTelemetry(
    options.vlCoreFactory(),
    options.playerId,
  ) as PlayerCoreWithGetPlayer;
  if (typeof core.init !== 'function') {
    throw new TypeError('vlPlayerCore.init must be a function');
  }
  const result = (await core.init(options.initConfig)) as TResult;
  const livePlayer = await waitForPlayerInstance(
    typeof core.getPlayer === 'function' ? core.getPlayer.bind(core) : undefined,
    options.playerId,
  );
  const coreForTelemetry: PlayerCoreWithGetPlayer = {
    ...core,
    init: () => Promise.resolve(result),
    getPlayer: (id?: string) =>
      livePlayer ??
      resolvePlayerInstanceForTelemetry(
        typeof core.getPlayer === 'function' ? core.getPlayer.bind(core) : undefined,
        id != null && String(id).trim() !== '' ? String(id) : options.playerId,
      ),
  };
  try {
    const created = options.createIntegration(coreForTelemetry);
    const initialized = await created.init();
    const integration =
      initialized != null &&
      typeof initialized === 'object' &&
      typeof (initialized as TelemetryIntegration).destroy === 'function'
        ? (initialized as TelemetryIntegration)
        : created;
    if (typeof integration.adapter?.init === 'function') {
      integration.adapter.init();
    }
    return { integration: integration as TIntegration, result };
  } catch (error) {
    console.warn('[playerTelemetry] telemetry attach failed; playback will continue', error);
    return { integration: null, result };
  }
};

function resolveTelemetryCountryCode(
  w: AppCmsWindow | undefined,
  explicit?: string | null
): string {
  if (explicit != null && String(explicit).trim() !== '') {
    return String(explicit).trim();
  }
  const fromLower =
    w?.countrycode != null && String(w.countrycode).trim() !== ''
      ? String(w.countrycode).trim()
      : undefined;
  if (fromLower) return fromLower;
  const fromCamel =
    w?.countryCode != null && String(w.countryCode).trim() !== ''
      ? String(w.countryCode).trim()
      : undefined;
  if (fromCamel) return fromCamel;
  const fromAppData =
    w?.app_data?.countryCode != null && String(w.app_data.countryCode).trim() !== ''
      ? String(w.app_data.countryCode).trim()
      : undefined;
  return fromAppData ?? 'US';
}

/** Ensures we have a callable VL player core factory (handles default export interop). */
function resolveVLPlayerCoreFactory(factory: unknown, context: string): () => unknown {
  if (typeof factory === 'function') {
    return factory as () => unknown;
  }
  if (
    factory != null &&
    typeof factory === 'object' &&
    'default' in factory &&
    typeof (factory as { default: unknown }).default === 'function'
  ) {
    return (factory as { default: () => unknown }).default;
  }
  if (
    factory != null &&
    typeof factory === 'object' &&
    typeof (factory as { init?: unknown }).init === 'function'
  ) {
    return () => factory;
  }
  const kind = factory === null || factory === undefined ? String(factory) : typeof factory;
  throw new TypeError(
    `${context}: vlPlayerCoreFactory must be a function (returning the VL player core). Got: ${kind}`
  );
}

function readSiteIdAndSite(
  w: AppCmsWindow | undefined,
  overrides?: { siteId?: string | null; site?: string | null }
): { siteId: string | undefined; site: string | undefined } {
  const rawId = overrides?.siteId ?? w?.app_data?.appcmsMain?.id;
  const rawSite = overrides?.site ?? w?.app_data?.appcmsMain?.site;
  const siteId =
    rawId != null && String(rawId).trim() !== '' ? String(rawId).trim() : undefined;
  const site =
    rawSite != null && String(rawSite).trim() !== '' ? String(rawSite).trim() : undefined;
  return { siteId, site };
}

function buildCommonIntegrationConfig(options: {
  countryCode?: string | null;
  endpoint: string;
  site?: string | null;
  siteId?: string | null;
  userId?: string | null;
}) {
  const { countryCode, endpoint, site: siteOverride, siteId: siteIdOverride, userId } = options;
  const userIdHash = userId ? md5(String(userId)) : 'guest_user';
  const w = readWindow();
  const resolvedCountryCode = resolveTelemetryCountryCode(w, countryCode);
  const { siteId, site } = readSiteIdAndSite(w, {
    siteId: siteIdOverride,
    site: siteOverride,
  });

  const traceParent = getWebTelemetry()?.getTraceContext() ?? undefined;
  const requestId = readTelemetryRequestId();
  return {
    countryCode: resolvedCountryCode,
    endpoint,
    ...(traceParent ? { traceParent } : {}),
    // Nested QoE envelope on `/telemetry` (the ~140 MetricsCollector fields).
    // Do not set `transport: 'otlp'` against tenant CMS — OTLP flattens that
    // payload and `/v1/*` is not deployed on cms.uat.* hosts.
    transport: 'otlp' as const,
    serviceName: 'viewlift.player',
    platform: readTelemetryPlatform(),
    ...(requestId != null ? { requestId } : {}),
    experimentFlags: { telemetry_sdk: 'v1' },
    ...(siteId != null ? { siteId } : {}),
    ...(site != null ? { site } : {}),
    userIdHash,
    windowRef: w,
  };
}

export async function attachShakaTelemetryIntegration<
  TResult = PlayerTelemetryInitResult
>(options: {
  playerId: string;
  vlPlayerCoreFactory: () => unknown;
  initConfig: ShakaTelemetryInitConfig;
  countryCode?: string | null;
  userId?: string | null;
  /** Full ingest URL. Defaults to {@link getTelemetryIngestEndpoint}. */
  telemetryEndpoint?: string;
  /** Overrides {@link window.app_data.appcmsMain.id} when set. */
  siteId?: string | null;
  /** Overrides {@link window.app_data.appcmsMain.site} when set. */
  site?: string | null;
}): Promise<{ integration: unknown; result: TResult }> {
  const {
    playerId,
    vlPlayerCoreFactory,
    initConfig,
    countryCode,
    userId,
    telemetryEndpoint,
    siteId: siteIdOverride,
    site: siteOverride,
  } = options;
  const endpoint = telemetryEndpoint ?? getTelemetryIngestEndpoint();
  const commonConfig = buildCommonIntegrationConfig({
    countryCode,
    endpoint,
    site: siteOverride,
    siteId: siteIdOverride,
    userId,
  });
  const w = commonConfig.windowRef;
  const vlPlayerConfig = {
    ...initConfig,
    chromecastReceiverId: w?.app_data?.appcmsMain?.features?.customReceiverId,
    airplay: w?.app_data?.appcmsMain?.features?.isAirPlayEnabled ?? true,
    chromecastTransport: 'caf-load',
  };
  const autoplayVal = (initConfig as { autoplay?: boolean }).autoplay;
  const vlCoreFactory = resolveVLPlayerCoreFactory(vlPlayerCoreFactory, 'attachShakaTelemetryIntegration');
  return initPlayerThenAttachTelemetry<unknown, TResult>({
    playerId,
    vlCoreFactory,
    initConfig: vlPlayerConfig,
    createIntegration: (vlPlayerCore) =>
      startPlayerTelemetry('shaka', {
        playerId,
        vlPlayerCore,
        vlPlayerConfig,
        autoApplyPolicy: true,
        autoplay: autoplayVal !== false,
        commonConfig,
      }),
  });
}

export async function attachVideoJSTelemetryIntegration<
  TResult = PlayerTelemetryInitResult
>(options: {
  playerId: string;
  vlPlayerCoreFactory: () => unknown;
  initConfig: VideoJSTelemetryInitConfig;
  countryCode?: string | null;
  userId?: string | null;
  telemetryEndpoint?: string;
  siteId?: string | null;
  site?: string | null;
}): Promise<{ integration: unknown; result: TResult }> {
  const {
    playerId,
    vlPlayerCoreFactory,
    initConfig,
    countryCode,
    userId,
    telemetryEndpoint,
    siteId,
    site,
  } = options;
  const endpoint = telemetryEndpoint ?? getTelemetryIngestEndpoint();
  const commonConfig = buildCommonIntegrationConfig({
    countryCode,
    endpoint,
    site,
    siteId,
    userId,
  });
  const autoplayVal = (initConfig as { autoplay?: boolean }).autoplay;
  const vlCoreFactory = resolveVLPlayerCoreFactory(vlPlayerCoreFactory, 'attachVideoJSTelemetryIntegration');
  return initPlayerThenAttachTelemetry<unknown, TResult>({
    playerId,
    vlCoreFactory,
    initConfig,
    createIntegration: (vlPlayerCore) =>
      startPlayerTelemetry('videojs', {
        playerId,
        vlPlayerCore,
        vlPlayerConfig: initConfig,
        autoApplyPolicy: false,
        autoplay: autoplayVal !== false,
        commonConfig,
      }),
  });
}

export async function attachBitmovinTelemetryIntegration<
  TResult = PlayerTelemetryInitResult
>(options: {
  playerId: string;
  vlPlayerCoreFactory: () => unknown;
  initConfig: BitmovinTelemetryInitConfig;
  countryCode?: string | null;
  userId?: string | null;
  telemetryEndpoint?: string;
  siteId?: string | null;
  site?: string | null;
}): Promise<{ integration: unknown; result: TResult }> {
  const {
    playerId,
    vlPlayerCoreFactory,
    initConfig,
    countryCode,
    userId,
    telemetryEndpoint,
    siteId,
    site,
  } = options;
  const endpoint = telemetryEndpoint ?? getTelemetryIngestEndpoint();
  const commonConfig = buildCommonIntegrationConfig({
    countryCode,
    endpoint,
    site,
    siteId,
    userId,
  });
  const autoplayVal = (initConfig as { autoplay?: boolean }).autoplay;
  const vlCoreFactory = resolveVLPlayerCoreFactory(vlPlayerCoreFactory, 'attachBitmovinTelemetryIntegration');
  return initPlayerThenAttachTelemetry<unknown, TResult>({
    playerId,
    vlCoreFactory,
    initConfig,
    createIntegration: (vlPlayerCore) =>
      startPlayerTelemetry('bitmovin', {
        playerId,
        vlPlayerCore,
        vlPlayerConfig: initConfig,
        autoApplyPolicy: false,
        autoplay: autoplayVal !== false,
        commonConfig,
      }),
  });
}

function startPlayerTelemetry(
  player: PlayerKind,
  options: {
    playerId: string;
    vlPlayerCore: unknown;
    vlPlayerConfig: Record<string, unknown>;
    autoApplyPolicy: boolean;
    autoplay: boolean;
    commonConfig: ReturnType<typeof buildCommonIntegrationConfig>;
  },
): TelemetryIntegration {
  const { commonConfig } = options;
  return {
    init: () =>
      initPlayerTelemetry({
        player,
        playerId: options.playerId,
        vlPlayerCore: options.vlPlayerCore,
        vlPlayerConfig: options.vlPlayerConfig,
        endpoint: commonConfig.endpoint,
        transport: commonConfig.transport,
        flushInterval: 5000,
        autoApplyPolicy: options.autoApplyPolicy,
        autoplay: options.autoplay,
        countryCode: commonConfig.countryCode,
        serviceName: commonConfig.serviceName,
        platform: commonConfig.platform,
        userIdHash: commonConfig.userIdHash,
        experimentFlags: commonConfig.experimentFlags,
        ...(commonConfig.requestId != null ? { requestId: commonConfig.requestId } : {}),
        ...(commonConfig.siteId != null ? { siteId: commonConfig.siteId } : {}),
        ...(commonConfig.site != null ? { site: commonConfig.site } : {}),
        ...(commonConfig.traceParent ? { traceParent: commonConfig.traceParent } : {}),
      } as never),
  };
}
